import React, { useState, useEffect } from 'react';
import { ModeType } from '../contexts/ModeContext';
import { useSpace } from '../contexts/SpaceContext';
import { Plus, Edit2, Trash2, Check, X, ChevronRight } from 'lucide-react';
import '../styles/components/bottomSheet.css';
import '../styles/components/appContextSheet.css';


interface AppContextSheetProps {
  isOpen: boolean;
  onClose: () => void;
  activeMode: ModeType;
  onModeSelect: (mode: ModeType) => void;
}

const AppContextSheet: React.FC<AppContextSheetProps> = ({ isOpen, onClose, activeMode, onModeSelect }) => {
  const { spaces, activeSpace, setActiveSpace, createSpace, updateSpace, deleteSpace } = useSpace();
  const [isCreatingSpace, setIsCreatingSpace] = useState(false);
  const [newSpaceName, setNewSpaceName] = useState('');
  const [editingSpaceId, setEditingSpaceId] = useState<number | null>(null);
  const [editSpaceName, setEditSpaceName] = useState('');
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // iOS keyboard handling - use Capacitor Keyboard plugin (same pattern as AddItemModal)
  useEffect(() => {
    if (!isOpen) return;
    const isCapacitor = document.documentElement.classList.contains('capacitor-app');
    if (!isCapacitor) return;

    try {
      const { Keyboard } = require('@capacitor/keyboard');
      const showListener = Keyboard.addListener('keyboardWillShow', (info: any) => {
        setKeyboardHeight(info.keyboardHeight || 260);
      });
      const hideListener = Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardHeight(0);
      });
      return () => {
        showListener.then((l: any) => l.remove());
        hideListener.then((l: any) => l.remove());
      };
    } catch (_) {
      // @capacitor/keyboard not available
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCreateSpace = async () => {
    if (!newSpaceName.trim()) return;
    try {
      const space = await createSpace(newSpaceName.trim());
      if (space) {
        setNewSpaceName('');
        setIsCreatingSpace(false);
      }
    } catch (err) {
      alert('Failed to create space');
    }
  };

  const handleUpdateSpace = async (id: number) => {
    if (!editSpaceName.trim()) return;
    try {
      await updateSpace(id, editSpaceName.trim());
      setEditingSpaceId(null);
      setEditSpaceName('');
    } catch (err) {
      alert('Failed to rename space');
    }
  };

  const handleDeleteSpace = async (id: number, name: string) => {
    if (window.confirm(`Delete "${name}"? Collections in this space will also be deleted.`)) {
      try {
        await deleteSpace(id);
      } catch (err: any) {
        alert(err?.message || 'Failed to delete space');
      }
    }
  };

  return (
    <div className="bottom-sheet-overlay open" onClick={onClose}>
      <div 
        className="bottom-sheet open app-context-sheet" 
        onClick={e => e.stopPropagation()}
        style={keyboardHeight > 0 ? { transform: `translateY(-${keyboardHeight}px)`, maxHeight: `calc(90vh - ${keyboardHeight}px)`, transition: 'transform 0.25s ease, max-height 0.25s ease' } : undefined}
      >
        <div className="bottom-sheet-handle-area">
          <div className="bottom-sheet-handle" />
        </div>
        
        <div className="bottom-sheet-header">
          <h2 className="bottom-sheet-title">DATA SPACE</h2>
          <button className="bottom-sheet-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="bottom-sheet-content app-context-content">
          <section className="context-section">
            <div className="section-header">
              <p className="section-hint">Isolate inventory by location or client. Perfect for professionals managing multiple clients or users with multiple homes.</p>
            </div>
            
            <div className="spaces-context-list">
              {spaces.map(space => (
                <div 
                  key={space.id} 
                  className={`space-context-item ${activeSpace?.id === space.id ? 'active' : ''} ${editingSpaceId === space.id ? 'editing' : ''}`}
                  onClick={() => {
                    if (editingSpaceId !== space.id) {
                      setActiveSpace(space);
                      onClose();
                    }
                  }}
                >
                  {editingSpaceId === space.id ? (
                    <div className="space-edit-inline" onClick={e => e.stopPropagation()}>
                      <input 
                        type="text" 
                        value={editSpaceName} 
                        onChange={e => setEditSpaceName(e.target.value)}
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleUpdateSpace(space.id);
                          if (e.key === 'Escape') setEditingSpaceId(null);
                        }}
                      />
                      <button className="icon-btn-small success" onClick={() => handleUpdateSpace(space.id)}>
                        <Check size={16} />
                      </button>
                      <button className="icon-btn-small cancel" onClick={() => setEditingSpaceId(null)}>
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="space-context-info">
                        <span className="space-context-name">{space.name}</span>
                        {activeSpace?.id === space.id && <span className="active-dot" />}
                      </div>
                      <div className="space-context-actions" onClick={e => e.stopPropagation()}>
                        <button 
                          className="icon-btn-small" 
                          onClick={() => {
                            setEditingSpaceId(space.id);
                            setEditSpaceName(space.name);
                          }}
                        >
                          <Edit2 size={14} />
                        </button>
                        {spaces.length > 1 && (
                          <button className="icon-btn-small delete" onClick={() => handleDeleteSpace(space.id, space.name)}>
                            <Trash2 size={14} />
                          </button>
                        )}
                        <ChevronRight size={16} className="item-chevron" />
                      </div>
                    </>
                  )}
                </div>
              ))}

              {isCreatingSpace ? (
                <div className="create-space-inline">
                  <input 
                    type="text" 
                    placeholder="New space name..." 
                    value={newSpaceName}
                    onChange={e => setNewSpaceName(e.target.value)}
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleCreateSpace();
                      if (e.key === 'Escape') setIsCreatingSpace(false);
                    }}
                  />
                  <div className="create-actions">
                    <button className="btn-small cancel" onClick={() => setIsCreatingSpace(false)}>Cancel</button>
                    <button className="btn-small primary" onClick={handleCreateSpace} disabled={!newSpaceName.trim()}>Add</button>
                  </div>
                </div>
              ) : (
                <button className="add-space-inline-btn" onClick={() => setIsCreatingSpace(true)}>
                  <Plus size={18} />
                  <span>Create New Space</span>
                </button>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AppContextSheet;
